import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FieldArray, Formik, useFormikContext } from "formik";
import * as Yup from "yup";

import { approveRequest, getRequest } from "src/api/inventory";
import { TextField } from "src/components/form";
import EmptyState from "src/components/common/EmptyState";
import { Button } from "src/components/ui/button";
import { usePortalContext } from "src/context/PortalContext";
import { renderNumber, showSuccessToast } from "src/lib/utils";
import type { StockRequestItem } from "src/types/inventory";

const validationSchema = Yup.object().shape({
	items: Yup.array()
		.of(
			Yup.object().shape({
				product_id: Yup.string().required("Please select a product"),
				qty: Yup.number().moreThan(0, "Quantity must be greater than 0"),
			})
		)
		.min(1, "At least one item must be provided"),
});

interface ApproveItem {
	product_id: string;
	qty: number;
	product: StockRequestItem;
}

interface ApproveFormValues {
	uuid: string;
	items: ApproveItem[];
}

interface ApproveRequestProps {
	requestUUID: string;
}

/** Approve a stock transfer, optionally fulfilling less than was requested. */
const ApproveRequest = ({ requestUUID }: ApproveRequestProps) => {
	const queryClient = useQueryClient();
	const closePortal = usePortalContext();

	const { data } = useQuery({
		queryKey: ["requests", requestUUID],
		queryFn: () => getRequest(requestUUID),
		suspense: true,
	});

	const details = data?.data;

	const initialValues: ApproveFormValues = {
		uuid: requestUUID,
		items:
			details?.items?.map((item) => ({
				product_id: String(item.product_id ?? ""),
				qty: item.requested_quantity ?? 0,
				product: item,
			})) ?? [],
	};

	const { mutate, isLoading } = useMutation(approveRequest, {
		onSuccess: () => {
			showSuccessToast("Request approved successfully");
			queryClient.invalidateQueries(["requests"]);
			closePortal();
		},
	});

	return (
		<Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={(values) => mutate(values)}>
			{({ values, handleSubmit }) => (
				<form onSubmit={handleSubmit} className="tw-grid tw-gap-4">
					<FieldArray
						name="items"
						render={() =>
							values.items.length === 0 ? (
								<EmptyState title="Nothing to approveRequest" description="This request has no items." />
							) : (
								<div className="tw-divide-y tw-divide-border tw-rounded-[10px] tw-border tw-border-border tw-px-4">
									{values.items.map((_, index) => (
										<ApproveItemRow key={index} index={index} />
									))}
								</div>
							)
						}
					/>

					<Button type="submit" variant="secondary" className="tw-mt-1 tw-w-full" disabled={isLoading}>
						{isLoading ? "Approving..." : "Approve request"}
					</Button>
				</form>
			)}
		</Formik>
	);
};

const ApproveItemRow = ({ index }: { index: number }) => {
	const { values, handleChange } = useFormikContext<ApproveFormValues>();

	const item = values.items[index];

	return (
		<div className="tw-flex tw-items-center tw-gap-3 tw-py-3">
			<div className="tw-min-w-0 tw-flex-1">
				<p className="tw-truncate tw-text-[14px] tw-font-medium tw-text-text">{item?.product?.name}</p>
				<p className="tw-text-[12px] tw-text-text-soft">Requested: {renderNumber(item?.product?.requested_quantity)}</p>
			</div>

			<TextField
				label="Approve"
				name={`items.${index}.qty`}
				value={item.qty}
				onChange={handleChange}
				type="number"
				inputMode="numeric"
				min={0}
				containerClassName="tw-w-[104px]"
				className="tw-h-9 tw-text-center"
			/>
		</div>
	);
};

export default ApproveRequest;
